import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Play, Pause, SkipForward, SkipBack, Rewind, FastForward } from 'lucide-react'

// Import Multitrack
declare global {
  interface Window {
    Multitrack: any
  }
}

// 重用 MixPlayerPage.tsx 中的类型定义
interface Track {
  artist: string
  song: string
  hash: string
  bpm: number
  sync_in?: {
    starttime: number
    endtime: number
    startbeat: number
    nbeats: number
  }
  sync_out?: {
    starttime: number
    endtime: number
    startbeat: number
    nbeats: number
  }
}

interface MixPair {
  pair_index: number
  mix_situation_name: string
  is_success: boolean
  error_code: number
  error_message: string
  // 添加新的statusCode字段支持
  statusCode?: number
  // 添加debug字段支持
  debug_status?: number
  debug_msg?: string
  mixtability: {
    bpm: {
      difference_percent: number
      threshold: number
      pass: boolean
    }
    genre: {
      compatibility_score: number
      compatibility_reason: string
      pass: boolean
    }
    score_factors: Array<{
      name: string
      score: number
      threshold: number
      pass: boolean
      description: string
    }>
    overall: {
      pass: boolean
      reason: string
      score: number
    }
  }
  bpm_difference_percent: number
  transition_nbeats: number
  automation_preset: string
  track_prev: Track
  track_next: Track
  merge_info: {
    presyncout_length_samples: number
    presyncout_length_seconds: number
    syncout_length_samples: number
    syncout_length_seconds: number
  }
}

interface QueueDetails {
  id: string
  name: string
  category: string
  instance: string
  tracks: Array<{ filename: string; hash: string; url: string }>
  transitions: Array<{ filename: string; url: string }>
  transitionsMerged?: Array<{ filename: string; url: string }>
  mixinfo: {
    mix_pairs: MixPair[]
    session_id: string
    sequencing_method: string
  }
  trackCount: number
  transitionCount: number
  transitionsMergedCount?: number
}

interface MultiTrackMixPlayerProps {
  queueDetails: QueueDetails
  currentPairIndex: number
  onPairChange: (index: number) => void
  audioPathPrefix?: string  // 可选的音频路径前缀
}

export const MultiTrackMixPlayer: React.FC<MultiTrackMixPlayerProps> = ({
  queueDetails,
  currentPairIndex,
  onPairChange,
  audioPathPrefix
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const multitrackRef = useRef<any>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [canPlay, setCanPlay] = useState(false)
  const [muteState, setMuteState] = useState<'both' | 'track0' | 'track1'>('both')
  // 新增：脚本加载状态
  const [scriptLoaded, setScriptLoaded] = useState(false)

  // Global keyboard event listener for space key and navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle keys if not typing in an input field
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return
      }

      switch (event.code) {
        case 'Space':
          event.preventDefault()
          console.log('Space key pressed - toggling play/pause')
          handlePlayPause()
          break
        case 'KeyQ': // Q key (was [ key)
          event.preventDefault()
          console.log('Q key pressed - going to previous pair')
          handlePrevious()
          break
        case 'KeyE': // E key (was ] key)
          event.preventDefault()
          console.log('E key pressed - going to next pair')
          handleNext()
          break
        case 'KeyA': // A key (was - key)
          event.preventDefault()
          console.log('A key pressed - seeking backward')
          handleSeek('backward')
          break
        case 'KeyD': // D key (was = key)
          event.preventDefault()
          console.log('D key pressed - seeking forward')
          handleSeek('forward')
          break
        case 'Digit1': // 1 key
          event.preventDefault()
          console.log('1 key pressed - muting track 1 (next track)')
          handleMute('track1')
          break
        case 'Digit2': // 2 key
          event.preventDefault()
          console.log('2 key pressed - muting track 0 (current track)')
          handleMute('track0')
          break
        case 'Digit3': // 3 key
          event.preventDefault()
          console.log('3 key pressed - unmuting both tracks')
          handleMute('both')
          break
        default:
          break
      }
    }

    // Add global keyboard event listener
    document.addEventListener('keydown', handleKeyDown)
    console.log('Global keyboard listeners added')

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      console.log('Global keyboard listeners removed')
    }
  }, [canPlay, currentPairIndex, queueDetails?.mixinfo?.mix_pairs?.length, muteState]) // Re-add listener when dependencies change

  // Calculate track positions based on mixinfo - using merged audio files
  const calculateTrackConfiguration = useCallback(() => {
    if (!queueDetails || !queueDetails.mixinfo) {
      console.log('No queue details or mixinfo available')
      return []
    }

    const currentPair = queueDetails.mixinfo.mix_pairs[currentPairIndex]
    if (!currentPair) {
      console.log('No current pair found at index:', currentPairIndex)
      return []
    }

    console.log('Current pair:', currentPair)

    // 检查debug_status，如果不为0则不加载音频
    if (currentPair.debug_status !== undefined && currentPair.debug_status !== 0) {
      console.log('Debug status is not 0, returning empty tracks for navigation only')
      return []
    }

    const tracks = []
    
    // Get merge info for positioning - 适配新数据结构
    const mergeInfo = currentPair.merge_info
    const presyncoutLengthSeconds = mergeInfo?.presyncout_length_seconds || 0
    
    console.log('Merge info:', mergeInfo, 'Presyncout length:', presyncoutLengthSeconds)

    // Configuration for merged_syncout (positioned at 0)
    const syncoutFilename = `${currentPair.pair_index}_${currentPair.track_prev.hash}_merged_syncout.mp3`
    // 使用自定义路径前缀或默认路径
    const syncoutUrl = audioPathPrefix 
      ? `${audioPathPrefix}/${syncoutFilename}`
      : `/audio/${queueDetails.instance}/${queueDetails.id}/transitions_merged/${syncoutFilename}`
    
    const syncoutConfig: any = {
      id: 0,
      url: syncoutUrl,
      draggable: false,
      startPosition: 0, // merged_syncout starts at position 0
      options: {
        waveColor: 'hsl(210, 87%, 49%)', // 蓝色
        progressColor: 'hsl(210, 87%, 20%)',
      }
    }
    tracks.push(syncoutConfig)
    console.log('Added merged_syncout config:', syncoutConfig)

    // Configuration for merged_syncin (positioned at presyncout_length_seconds)
    // Syncin 使用 pair_index + 1 作为文件名前缀
    const syncinFilename = `${currentPair.pair_index + 1}_${currentPair.track_next.hash}_merged_syncin.mp3`
    // 使用自定义路径前缀或默认路径
    const syncinUrl = audioPathPrefix 
      ? `${audioPathPrefix}/${syncinFilename}`
      : `/audio/${queueDetails.instance}/${queueDetails.id}/transitions_merged/${syncinFilename}`
    
    const syncinConfig: any = {
      id: 1,
      url: syncinUrl,
      draggable: false,
      startPosition: presyncoutLengthSeconds, // merged_syncin starts at presyncout_length_seconds
      options: {
        waveColor: 'hsl(270, 87%, 49%)', // 紫色
        progressColor: 'hsl(270, 87%, 20%)',
      }
    }
    tracks.push(syncinConfig)
    console.log('Added merged_syncin config:', syncinConfig)

    console.log('Final tracks configuration:', tracks)
    return tracks
  }, [queueDetails, currentPairIndex, audioPathPrefix])

  // Initialize multitrack
  useEffect(() => {
    console.log('Multitrack init effect triggered')
    console.log('Script loaded:', scriptLoaded)
    console.log('Container ref:', !!containerRef.current)
    console.log('Queue details:', !!queueDetails)
    console.log('Current pair index:', currentPairIndex)
    
    // 检查所有必要条件
    if (!containerRef.current) {
      console.log('No container ref available')
      return
    }
    
    if (!scriptLoaded || !window.Multitrack) {
      console.log('Multitrack library not loaded yet')
      return
    }

    if (!queueDetails || !queueDetails.mixinfo || !queueDetails.mixinfo.mix_pairs) {
      console.log('Queue details not ready yet')
      return
    }

    const currentPair = queueDetails.mixinfo.mix_pairs[currentPairIndex]
    if (!currentPair) {
      console.log('Current pair not found at index:', currentPairIndex)
      return
    }

    const tracks = calculateTrackConfiguration()
    console.log('Calculated tracks:', tracks)
    
    // 检查当前pair的debug状态
    const hasDebugError = currentPair.debug_status !== undefined && currentPair.debug_status !== 0
    
    setIsLoading(true)
    setCanPlay(false)
    setIsPlaying(false)

    // Destroy previous instance
    if (multitrackRef.current) {
      console.log('Destroying previous multitrack instance')
      multitrackRef.current.destroy()
    }

    try {
      if (hasDebugError || tracks.length === 0) {
        // 创建一个空的multitrack实例以保持导航功能
        console.log('Creating empty multitrack instance for navigation (debug error or no tracks)')
        multitrackRef.current = window.Multitrack.create([], {
          container: containerRef.current,
          minPxPerSec: 10,
          rightButtonDrag: false,
          cursorWidth: 2,
          cursorColor: '#D72F21',
          trackBackground: '#2D2D2D',
          trackBorderColor: '#7C7C7C',
          dragBounds: true,
        })

        // 立即设置为不加载状态，但保持导航功能
        setIsLoading(false)
        setCanPlay(false) // 不允许播放，但保持导航
        setDuration(0)
      } else {
        // 正常创建带音频的multitrack实例
        console.log('Creating new multitrack instance with tracks:', tracks)
        multitrackRef.current = window.Multitrack.create(tracks, {
          container: containerRef.current,
          minPxPerSec: 10,
          rightButtonDrag: false,
          cursorWidth: 2,
          cursorColor: '#D72F21',
          trackBackground: '#2D2D2D',
          trackBorderColor: '#7C7C7C',
          dragBounds: true,
        })

        console.log('Multitrack instance created:', multitrackRef.current)

        // Set up event listeners
        multitrackRef.current.on('canplay', () => {
          console.log('Multitrack can play')
          setIsLoading(false)
          setCanPlay(true)
          setDuration(multitrackRef.current.getDuration())
        })

        multitrackRef.current.on('timeupdate', () => {
          setCurrentTime(multitrackRef.current.getCurrentTime())
        })

        multitrackRef.current.on('play', () => {
          console.log('Multitrack started playing')
          setIsPlaying(true)
        })
        
        multitrackRef.current.on('pause', () => {
          console.log('Multitrack paused')
          setIsPlaying(false)
        })

        multitrackRef.current.on('audioprocess', () => {
          const isCurrentlyPlaying = multitrackRef.current.isPlaying()
          if (isCurrentlyPlaying !== isPlaying) {
            console.log('Play state changed:', isCurrentlyPlaying)
            setIsPlaying(isCurrentlyPlaying)
          }
        })

        multitrackRef.current.on('finish', () => {
          console.log('Track finished')
          setIsPlaying(false)
        })

        // 添加错误处理
        multitrackRef.current.on('error', (error: any) => {
          console.error('Multitrack error:', error)
          setIsLoading(false)
        })
      }

    } catch (error) {
      console.error('Error creating multitrack:', error)
      setIsLoading(false)
    }

    return () => {
      if (multitrackRef.current) {
        console.log('Cleaning up multitrack instance')
        multitrackRef.current.destroy()
      }
    }
  }, [scriptLoaded, queueDetails, currentPairIndex, calculateTrackConfiguration])

  // Load multitrack script
  useEffect(() => {
    if (window.Multitrack) {
      setScriptLoaded(true)
      return
    }

    const script = document.createElement('script')
    script.src = '/multitrack.min.js'
    script.onload = () => {
      console.log('Multitrack script loaded')
      setScriptLoaded(true)
    }
    script.onerror = () => {
      console.error('Failed to load Multitrack script')
      setScriptLoaded(false)
    }
    document.head.appendChild(script)

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script)
      }
    }
  }, [])

  const handlePlayPause = () => {
    if (!multitrackRef.current || !canPlay) {
      console.log('Cannot play/pause - multitrack not ready')
      return
    }

    const currentPlayingState = multitrackRef.current.isPlaying()
    console.log('Toggling play/pause, current state:', currentPlayingState)
    
    if (currentPlayingState) {
      multitrackRef.current.pause()
      setIsPlaying(false) // Immediately update UI state
    } else {
      multitrackRef.current.play()
      setIsPlaying(true) // Immediately update UI state
    }
  }

  const handlePrevious = () => {
    if (currentPairIndex > 0) {
      onPairChange(currentPairIndex - 1)
    }
  }

  const handleNext = () => {
    if (currentPairIndex < queueDetails.mixinfo.mix_pairs.length - 1) {
      onPairChange(currentPairIndex + 1)
    }
  }

  const handleSeek = (direction: 'forward' | 'backward') => {
    if (!multitrackRef.current || !canPlay) {
      console.log('Cannot seek - multitrack not ready')
      return
    }
    
    const currentTime = multitrackRef.current.getCurrentTime()
    const newTime = direction === 'forward' ? currentTime + 8 : currentTime - 8
    const seekTime = Math.max(0, newTime)
    
    console.log(`Seeking ${direction} from ${currentTime}s to ${seekTime}s`)
    multitrackRef.current.setTime(seekTime)
  }



  const handleMute = (newMuteState: 'both' | 'track0' | 'track1') => {
    if (!multitrackRef.current || !canPlay) {
      console.log('Cannot change mute state - multitrack not ready')
      return
    }

    console.log('Changing mute state from', muteState, 'to', newMuteState)
    setMuteState(newMuteState)

    // Apply volume changes based on mute state
    switch (newMuteState) {
      case 'track0':
        // Mute track 0 (current track), unmute track 1
        multitrackRef.current.setTrackVolume(0, 0)
        multitrackRef.current.setTrackVolume(1, 1)
        console.log('Muting track 0, unmuting track 1')
        break
      case 'track1':
        // Mute track 1 (next track), unmute track 0
        multitrackRef.current.setTrackVolume(0, 1)
        multitrackRef.current.setTrackVolume(1, 0)
        console.log('Muting track 1, unmuting track 0')
        break
      case 'both':
        // Unmute both tracks
        multitrackRef.current.setTrackVolume(0, 1)
        multitrackRef.current.setTrackVolume(1, 1)
        console.log('Unmuting both tracks')
        break
    }
  }

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
  }

  const currentPair = queueDetails.mixinfo.mix_pairs[currentPairIndex]

  return (
    <div className="w-full space-y-4">

             {/* Multitrack Container */}
       <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden relative">
         <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
           <div className="flex items-center justify-between">
             {/* All Controls - Moved to left */}
             <div className="flex items-center space-x-3">
               {/* Previous Track */}
               <button
                 onClick={handlePrevious}
                 disabled={currentPairIndex === 0}
                 className="p-2 rounded bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                 title="上一对 ([)"
               >
                 <SkipBack className="h-4 w-4" />
               </button>
               
               {/* Seek Backward */}
               <button
                 onClick={() => handleSeek('backward')}
                 disabled={!canPlay}
                 className="p-2 rounded bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                 title="快退8秒"
               >
                 <Rewind className="h-4 w-4" />
               </button>
               
               {/* Play/Pause */}
               <button
                 onClick={handlePlayPause}
                 disabled={!canPlay}
                 className={`p-2 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                   isPlaying 
                     ? 'bg-red-600 hover:bg-red-700' 
                     : 'bg-primary-600 hover:bg-primary-700'
                 }`}
                 title="播放/暂停 (空格键)"
               >
                 {isLoading ? (
                   <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                 ) : isPlaying ? (
                   <Pause className="h-4 w-4" />
                 ) : (
                   <Play className="h-4 w-4" />
                 )}
               </button>
               
               {/* Seek Forward */}
               <button
                 onClick={() => handleSeek('forward')}
                 disabled={!canPlay}
                 className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                 title="快进8秒"
               >
                 <FastForward className="h-4 w-4" />
               </button>
               
               {/* Next Track */}
               <button
                 onClick={handleNext}
                 disabled={currentPairIndex === queueDetails.mixinfo.mix_pairs.length - 1}
                 className="p-2 rounded bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                 title="下一对 (])"
               >
                 <SkipForward className="h-4 w-4" />
               </button>
             </div>
             
             {/* Track Info - Moved to right */}
             <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
               <div className="flex items-center space-x-1">
                 <div className="w-3 h-3 bg-blue-400 rounded"></div>
                 <span>Prev Track</span>
                 {muteState === 'track0' && <span className="text-red-600 font-bold">[静音]</span>}
               </div>
               <div className="flex items-center space-x-1">
                 <div className="w-3 h-3 bg-purple-400 rounded"></div>
                 <span>Next Track</span>
                 {muteState === 'track1' && <span className="text-red-600 font-bold">[静音]</span>}
               </div>
               {muteState === 'both' && (
                 <div className="text-green-600 font-bold">[双轨播放]</div>
               )}
             </div>
           </div>
         </div>
          
         <div 
           ref={containerRef}
           className="w-full"
           style={{ background: '#2d2d2d', color: '#fff', minHeight: '200px' }}
         />
         
                 {isLoading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2" />
              <div>Loading multitrack...</div>
              <div className="text-sm mt-1 opacity-75">请稍候，正在初始化多轨道播放器</div>
            </div>
          </div>
        )}
        
        {/* Debug Error Display */}
        {!isLoading && currentPair && currentPair.debug_status !== undefined && currentPair.debug_status !== 0 && (
          <div className="absolute inset-0 bg-gray-800 bg-opacity-90 flex items-center justify-center">
            <div className="text-center text-white p-6">
              <div className="text-yellow-400 text-lg font-medium mb-2">调试模式 - 音频未加载</div>
              <div className="text-sm text-gray-300 mb-4">
                当前Mix Pair存在调试状态错误 (Code: {currentPair.debug_status})
              </div>
              <div className="text-xs text-gray-400">
                ⚠️ 音频播放功能已禁用，但导航功能正常可用
              </div>
              <div className="text-xs text-gray-500 mt-2">
                使用 Q/E 键继续浏览其他 Mix Pair
              </div>
            </div>
          </div>
        )}
         
         {/* Keyboard shortcuts - moved below waveform */}
         <div className="px-4 py-2 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600">
           <div className="flex items-center space-x-6 text-xs text-gray-600 dark:text-gray-400">
             <div className="flex items-center space-x-1">
               <kbd className="px-2 py-1 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded text-xs font-mono">{'Space'}</kbd>
               <span>播放/暂停</span>
             </div>
             <div className="flex items-center space-x-1">
               <kbd className="px-2 py-1 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded text-xs font-mono">Q</kbd>
               <span>上一对</span>
             </div>
             <div className="flex items-center space-x-1">
               <kbd className="px-2 py-1 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded text-xs font-mono">E</kbd>
               <span>下一对</span>
             </div>
             <div className="flex items-center space-x-1">
               <kbd className="px-2 py-1 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded text-xs font-mono">A</kbd>
               <span>快退8秒</span>
             </div>
             <div className="flex items-center space-x-1">
               <kbd className="px-2 py-1 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded text-xs font-mono">D</kbd>
               <span>快进8秒</span>
             </div>
             <div className="flex items-center space-x-1">
               <kbd className="px-2 py-1 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded text-xs font-mono">1</kbd>
               <span>静音Next Track</span>
             </div>
             <div className="flex items-center space-x-1">
               <kbd className="px-2 py-1 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded text-xs font-mono">2</kbd>
               <span>静音Prev Track</span>
             </div>
             <div className="flex items-center space-x-1">
               <kbd className="px-2 py-1 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded text-xs font-mono">3</kbd>
               <span>取消静音</span>
             </div>
           </div>
         </div>
         
         
       </div>
    </div>
  )
}
